/**
 * ComparisonPractice - Main orchestrator for K Comparison practice
 *
 * Manages the flow through I Do -> We Do -> You Do -> Accuracy -> Fluency15 -> Fluency screens
 * Supports initialPracticeType prop for routing directly to accuracy/fluency15/fluency screens
 */

import React, { useState, useEffect, useCallback } from 'react';
import { PracticeScreen, KPracticeSettings } from '../types';
import { ComparisonFact } from './types';
import { getTrackFacts } from '../../../config/api';
import IDoScreen from './IDoScreen';
import WeDoScreen from './WeDoScreen';
import YouDoScreen from './YouDoScreen';
import AccuracyScreen from './AccuracyScreen';
import Fluency15Screen from './Fluency15Screen';
import FluencyScreen from './FluencyScreen';

interface ComparisonPracticeProps {
  settings: KPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
  initialPracticeType?: 'learning' | 'accuracy' | 'fluency15' | 'fluency';
}

const ComparisonPractice: React.FC<ComparisonPracticeProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
  initialPracticeType = 'learning',
}) => {
  // Determine initial screen based on practice type
  const getInitialScreen = (): PracticeScreen => {
    switch (initialPracticeType) {
      case 'accuracy':
        return 'accuracy';
      case 'fluency15':
        return 'fluency15';
      case 'fluency':
        return 'fluency';
      default:
        return 'ido';
    }
  };

  const [currentScreen, setCurrentScreen] = useState<PracticeScreen>(getInitialScreen());
  const [comparisonFacts, setComparisonFacts] = useState<ComparisonFact[]>([]);
  const [allTrackFacts, setAllTrackFacts] = useState<ComparisonFact[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load comparison facts from backend
  useEffect(() => {
    const loadComparisonFacts = async () => {
      try {
        setIsLoading(true);
        const facts = await getTrackFacts('TRACK_K_COMPARING');

        // Store ALL track facts for accuracy/fluency15 screens
        const allFacts = facts.map((fact) => ({
          factId: fact.factId,
          operand1: fact.operand1,
          operand2: fact.operand2,
          result: fact.result, // The larger number
        }));
        setAllTrackFacts(allFacts);

        // Filter to only include facts that need practice for learning screens
        const filteredFacts = facts
          .filter((fact) => factsToPractice.includes(fact.factId))
          .map((fact) => ({
            factId: fact.factId,
            operand1: fact.operand1,
            operand2: fact.operand2,
            result: fact.result, // The larger number
          }));

        setComparisonFacts(filteredFacts);
      } catch (error) {
        console.error('Error loading comparison facts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadComparisonFacts();
  }, [factsToPractice]);

  // Handle empty comparisonFacts - navigate back via useEffect to avoid side effects during render
  // Only check for learning screens; accuracy/fluency15/fluency use allTrackFacts
  useEffect(() => {
    const isLearningScreen = initialPracticeType === 'learning';
    const hasNoLearningFacts = comparisonFacts.length === 0 && isLearningScreen;
    const hasNoTrackFacts = allTrackFacts.length === 0 && !isLearningScreen;

    if (!isLoading && (hasNoLearningFacts || hasNoTrackFacts)) {
      console.error('ComparisonPractice: No comparison facts loaded');
      onBack();
    }
  }, [comparisonFacts.length, allTrackFacts.length, isLoading, onBack, initialPracticeType]);

  // Handle I Do screen completion
  const handleIDoComplete = useCallback(() => {
    setCurrentScreen('wedo');
  }, []);

  // Handle We Do screen completion
  const handleWeDoComplete = useCallback(() => {
    setCurrentScreen('youdo');
  }, []);

  // Handle You Do screen completion
  const handleYouDoComplete = useCallback(() => {
    setCurrentScreen('accuracy');
  }, []);

  // Handle Accuracy screen completion - page reload handles routing
  const handleAccuracyComplete = useCallback(() => {
    window.location.reload();
  }, []);

  // Handle Fluency15 screen completion - page reload handles routing
  const handleFluency15Complete = useCallback(() => {
    window.location.reload();
  }, []);

  // Handle Fluency screen completion
  const handleFluencyComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading comparison facts...</div>
      </div>
    );
  }

  // Guard against empty facts - return stable fallback UI
  const isLearningScreen = currentScreen === 'ido' || currentScreen === 'wedo' || currentScreen === 'youdo';
  if (isLearningScreen && comparisonFacts.length === 0) {
    return null;
  }
  if (!isLearningScreen && allTrackFacts.length === 0) {
    return null;
  }

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'ido':
        return (
          <IDoScreen
            settings={settings}
            factsToPractice={comparisonFacts}
            onComplete={handleIDoComplete}
            onBack={onBack}
          />
        );

      case 'wedo':
        return (
          <WeDoScreen
            settings={settings}
            factsToPractice={comparisonFacts}
            onComplete={handleWeDoComplete}
            onBack={onBack}
          />
        );

      case 'youdo':
        return (
          <YouDoScreen
            settings={settings}
            factsToPractice={comparisonFacts}
            onComplete={handleYouDoComplete}
            onBack={onBack}
          />
        );

      case 'accuracy':
        // Use ALL track facts for accuracy practice (same as K Subitizing)
        return (
          <AccuracyScreen
            settings={settings}
            factsToPractice={allTrackFacts}
            onComplete={handleAccuracyComplete}
            onBack={onBack}
          />
        );

      case 'fluency15':
        // Use ALL track facts for fluency15 practice (same as K Subitizing)
        return (
          <Fluency15Screen
            settings={settings}
            factsToPractice={allTrackFacts}
            onComplete={handleFluency15Complete}
            onBack={onBack}
          />
        );

      case 'fluency':
        return (
          <FluencyScreen
            settings={settings}
            factsToPractice={comparisonFacts}
            onComplete={handleFluencyComplete}
            onBack={onBack}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      {renderCurrentScreen()}
    </div>
  );
};

export default ComparisonPractice;
